import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListPagination } from "@/components/shared/ListPagination";
import {
  useTransactionList,
  type TransactionRow,
  type CollectMetadata,
} from "@/hooks/useTransactions";
import { formatCurrency, formatDateTime } from "@/utils/format";
import { useState } from "react";

const asCollectMeta = (m: unknown): CollectMetadata | null => {
  if (m && typeof m === "object" && "collectedBy" in m)
    return m as CollectMetadata;
  return null;
};

const TYPE_LABELS: Record<string, string> = {
  INITIAL: "Initial",
  RENEWAL: "Renewal",
  UPGRADE_PRORATION: "Upgrade",
  REFUND: "Refund",
  MANUAL: "Manual",
};

const columns: Array<DataTableColumn<TransactionRow>> = [
  {
    key: "owner",
    header: "Provider",
    render: (t) => (
      <span className="font-medium">{t.owner?.name ?? t.ownerId}</span>
    ),
  },
  {
    key: "type",
    header: "Type",
    render: (t) => {
      const meta = asCollectMeta(t.metadata);
      return (
        <div>
          <span className="text-sm">{TYPE_LABELS[t.type] ?? t.type}</span>
          {meta?.month && (
            <span className="block text-xs text-muted-foreground">
              {meta.month}
            </span>
          )}
        </div>
      );
    },
  },
  {
    key: "gateway",
    header: "Gateway",
    render: (t) => (
      <span className="text-sm capitalize">{t.gateway.toLowerCase()}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    render: (t) => <StatusBadge status={t.status} />,
  },
  {
    key: "amount",
    header: "Amount",
    align: "right",
    render: (t) => (
      <span className="font-medium">{formatCurrency(t.amount)}</span>
    ),
  },
  {
    key: "collectedBy",
    header: "Collected by",
    render: (t) => {
      const meta = asCollectMeta(t.metadata);
      return meta?.collectedBy ? (
        <span className="text-sm">{meta.collectedBy.name}</span>
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      );
    },
  },
  {
    key: "paidAt",
    header: "Paid at",
    render: (t) => (
      <span className="text-muted-foreground text-sm">
        {t.paidAt ? formatDateTime(t.paidAt) : "—"}
      </span>
    ),
  },
];

const DEFAULT_LIMIT = 25;

export const RevenueTable = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const { data, isLoading, error, refetch } = useTransactionList({
    page,
    limit,
  });
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  if (isLoading) {
    return <div className="h-48 animate-pulse bg-muted rounded-card" />;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive text-sm">
        Failed to load transactions.{" "}
        <button type="button" onClick={() => refetch()} className="underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        selection="none"
      />
      <ListPagination
        pagination={pagination}
        page={page}
        rowsShown={rows.length}
        itemLabel="transactions"
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(n) => {
          setLimit(n);
          setPage(1);
        }}
      />
    </div>
  );
};
